import { TriangleDownIcon, XCircleFillIcon } from "@primer/octicons-react";
import { Box, Button, SelectPanel } from "@primer/react";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { hinhThucNopBomApi } from "../../api/hinhThucNopBom";
import { sf_hinhthucnopbom } from "../../models/response/category/sf_hinhthucnopbom";
import { RootState } from "../../state/reducers";

type SelectBoxMultipleHinhThucNopBomProps = {
  isReadonly?: boolean;
  value?: number[];
  onValueChanged: (ids: number[], data?: sf_hinhthucnopbom[]) => void;
  className?: string;
  preText?: string;
  width?: string | number;
  stylingMode?: "outlined" | "filled" | "underlined";
  isShowClearBtn?: boolean;
  maxWidth?: any;
  size?: "small" | "medium" | "large";
  dmCoSoId?: number;
  title?: string;
};

const SelectBoxMultipleHinhThucNopBom = (
  props: SelectBoxMultipleHinhThucNopBomProps
) => {
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [hinhThucNopBoms, setHinhThucNopBoms] = useState<sf_hinhthucnopbom[]>(
    []
  );
  useEffect(() => {
    handleReloadHinhThucNopBoms();
  }, []);
  const handleReloadHinhThucNopBoms = async () => {
    const res = await hinhThucNopBomApi.selectAll();
    setHinhThucNopBoms(res.data.filter((x: any) => x.id > 0));
  };

  const dataSource = useMemo(() => {
    let data = hinhThucNopBoms;
    return data.map((x) => ({
      id: x.id,
      text: `${x.name}`,
    }));
  }, [hinhThucNopBoms]);

  const removeVietnameseTones = (str: string) => {
    return str
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D");
  };

  const filteredData = useMemo(() => {
    const normalizedFilter = removeVietnameseTones(filter.toLowerCase());
    return dataSource.filter((item) =>
      removeVietnameseTones(item.text.toLowerCase()).includes(normalizedFilter)
    );
  }, [dataSource, filter]);

  const _selectedData = useMemo(() => {
    return dataSource.filter((item) => props.value?.includes(item.id));
  }, [props.value, dataSource]);

  const displayText = useMemo(() => {
    if (!_selectedData.length) {
      return props.title ?? "Chọn hình thức nộp";
    }

    // Nếu chọn tất cả
    if (_selectedData.length === dataSource.length && dataSource.length > 0) {
      return "Tất cả";
    }

    if (_selectedData.length > 4) {
      return `${_selectedData.length} items selected`;
    }

    return _selectedData.map(item => item.text).join(", ");
  }, [_selectedData, dataSource.length]);

  const handleSelectAll = () => {
    const allIds = dataSource.map(item => item.id);
    const allData = hinhThucNopBoms.filter(item => allIds.includes(item.id));
    props.onValueChanged(allIds, allData);
  };

  const isAllSelected = _selectedData.length === dataSource.length && dataSource.length > 0;

  return (
    <SelectPanel
      renderAnchor={({
        children,
        "aria-labelledby": ariaLabelledBy,
        ...anchorProps
      }) => (
        <Button
          sx={{
            width: props.width ?? "100%",
            display: "flex",
            justifyContent: "space-between",
            fontSize: props.size === "small" ? "12px" : "14px",
          }}
          size={props.size}
          trailingAction={TriangleDownIcon}
          aria-labelledby={ariaLabelledBy}
          {...anchorProps}
        >
          <p
            style={{
              maxWidth: props.maxWidth,
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: props.size === "small" ? "12px" : "14px",
              whiteSpace: "nowrap",
            }}
          >
            {displayText}
          </p>
        </Button>
      )}
      title={
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flex: 1 }}>{props.title ?? "Chọn hình thức nộp"}</Box>
          {dataSource.length > 0 && !isAllSelected && (
            <Button
              variant="invisible"
              sx={{ color: "accent.emphasis", fontSize: "12px" }}
              onClick={handleSelectAll}
            >
              Chọn tất cả
            </Button>
          )}
          {props.isShowClearBtn && (props.value?.length ?? 0) > 0 && (
            <Button
              trailingVisual={XCircleFillIcon}
              variant="invisible"
              sx={{ color: "danger.emphasis", fontSize: "12px" }}
              onClick={() => props.onValueChanged([])}
            >
              Bỏ chọn
            </Button>
          )}
        </Box>
      }
      placeholderText="Search"
      open={open}
      onOpenChange={setOpen}
      items={filteredData}
      selected={_selectedData}
      onSelectedChange={(selectedItems: any[]) => {
        const newValues = selectedItems.map((item) => item.id);
        const selectedData = hinhThucNopBoms.filter((item) =>
          newValues.includes(item.id)
        );
        props.onValueChanged(newValues, selectedData);
      }}
      onFilterChange={setFilter}
      showItemDividers={true}
      overlayProps={{ width: "large", height: "medium" }}
    />
  );
};

export default SelectBoxMultipleHinhThucNopBom;
